import cn from "classnames";
import { FC, Fragment, type ReactNode } from "react";

type TimelineProps = {
  items: ReactNode[];
  connectorDots?: {
    left?: ReactNode;
    right?: ReactNode;
  }[];
  connectorOverlays?: ReactNode[];
  direction?: "horizontal" | "vertical";
  overflowIndicator?: ReactNode;
  maxVisibleItems?: number;
  className?: string;
  classNames?: {
    item?: string;
    connector?: {
      rail?: string;
      head?: string;
      overlay?: string;
    };
  };
};

const Timeline: FC<TimelineProps> = ({
  items = [],
  className,
  direction = "horizontal",
  overflowIndicator,
  maxVisibleItems,
  connectorOverlays = [],
  classNames = {
    connector: {
      head: "h-3 w-3 rounded-full",
    },
  },
  connectorDots = [],
}) => {
  const isVertical = direction === "vertical";

  const isCollapsed =
    maxVisibleItems &&
    overflowIndicator &&
    items.length > maxVisibleItems;

  const renderedItems = isCollapsed
    ? [items[0], items[items.length - 1]]
    : items;

  return (
    <div
      className={cn(
        "flex w-full",
        isVertical
          ? "flex-col gap-4 items-start"
          : "items-center gap-4",
        className
      )}
    >
      {renderedItems.map((item, index) => (
        <Fragment key={index}>
          <div
            className={cn(
              "flex items-center justify-center",
              !isVertical && "min-w-0",
              classNames?.item
            )}
          >
            {item}
          </div>

          {index < renderedItems.length - 1 && (
            <div
              className={cn(
                "relative flex items-center justify-between min-w-0",
                isVertical
                  ? "flex-col w-0.5 flex-none h-20"
                  : "flex-1 h-0.5",
                classNames?.connector?.rail,
                !classNames?.connector?.rail && "bg-background-disabled"
              )}
            >
              {connectorOverlays[index] && (
                <div
                  className={cn(
                    "absolute flex items-center justify-center z-10",
                    isVertical
                      ? "left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2"
                      : "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
                    classNames?.connector?.overlay
                  )}
                >
                  {connectorOverlays[index]}
                </div>
              )}

              {isCollapsed ? (
                <div className={cn("absolute inset-0 flex items-center justify-center z-10", classNames.connector?.overlay)}>
                    {overflowIndicator}
                </div>
              ) : (
                <>
                  {connectorDots[index]?.left || (
                    <div
                      className={cn(
                        "h-2 w-2 rounded-full bg-current",
                        !isVertical && "-translate-x-1 rtl:translate-x-1",
                        classNames?.connector?.head
                      )}
                    />
                  )}

                  {connectorDots[index]?.right || (
                    <div
                      className={cn(
                        "h-2 w-2 rounded-full bg-current",
                        !isVertical && "translate-x-1 rtl:-translate-x-1",
                        classNames?.connector?.head
                      )}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </Fragment>
      ))}
    </div>
  );
};

export default Timeline;